import { useSession } from 'next-auth/react'
import { useTheme } from 'styled-components'

import { FeedFallbackComponents } from '@/components/homepage/feed-fallback-components/FeedFallbackComponents'
import { HomepageFeed } from '@/components/homepage/homepage-feed/HomepageFeed'
import { HomepageH1 } from '@/components/homepage/homepage-inner/HomepageH1'
import { PriceDrop } from '@/components/homepage/price-drop/PriceDrop'
import { SimilarToFavouritesGallery } from '@/components/homepage/similar-to-favourites/SimilarToFavouritesGallery'
import {
  HomepageAside,
  HomepageContainer,
  HomepageGptRightrailTopWrapper,
  HomepageMain,
  HomepageSectionWrapper,
} from '@/components/homepage/styled'
import { useHomepageInfiniteFeed } from '@/components/homepage/useHomepageInfiniteFeed'
import { ClientRender } from '@/components/shared/client-render'
import { BaseLayout } from '@/components/shared/layouts/BaseLayout'
import { GptMasthead } from '@/components-page/homepage/advertisement/GptMasthead'
import { GptRightrailMarketing } from '@/components-page/homepage/advertisement/GptRightrailMarketing'
import { GptRightrailTop } from '@/components-page/homepage/advertisement/GptRightrailTop'
import { GptStickyHP } from '@/components-page/homepage/advertisement/GptStickyHP'
import { HomepageAdsWrapper } from '@/components-page/homepage/advertisement/HomepageAdsWrapper'
import { HomepageSeoMetadata } from '@/components-page/homepage/seo/HomepageSeoMetadata'
import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useHomepageGetSeoUrl } from '@/hooks/useHomepageGetSeoUrl'
import { useLocale } from '@/hooks/useLocale'
import { RemoteParamKeys, useExperiment, useToggle } from '@/lib/firebase/hooks'
import { GA_PAGE_TYPE } from '@/lib/ga/constants/datalayer'
import { useTracking } from '@/lib/ga/hooks/useTracking'
import { Spacing } from '@/ui/atoms/spacing'
import { ScrollToTopButton } from '@/ui/molecules/scroll-to-top-button'

type HomepageInnerProps = {
  correlationId: string
  userAgent: string
}

export const HomepageInner = ({ correlationId, userAgent }: HomepageInnerProps) => {
  const { spacing } = useTheme()
  const { apiLocale } = useLocale()
  const { status: userStatus, data: userData } = useSession()
  const isUserLoggedIn = isUserAuthenticated(userStatus)
  const { location } = useGetLocation()
  const priceDropToggle = useToggle(RemoteParamKeys.PRICEDROP_NOTIFICATION)
  const mastheadToggle = useToggle(RemoteParamKeys.HOMEPAGE_MASTHEAD_TEST_TOGGLE)
  const mastheadExperiment = useExperiment(RemoteParamKeys.HOMEPAGE_MASTHEAD_TEST)
  const shouldShowMasthead = mastheadToggle?.enabled && mastheadExperiment?.group === 1
  const locationId = location.id

  useTracking({ pageType: GA_PAGE_TYPE.HOMEPAGE, categoryId: ALL_CATEGORIES_ID_NUM })

  const {
    feedPageInfo,
    feedResults,
    handleLoadMore,
    loadingFeed,
    loadingMore: hpFeedLoadingMore,
    shouldAutoFetchMore,
    shouldShowHomepageFeed,
  } = useHomepageInfiniteFeed({
    correlationId,
    locationArea: location.area,
    locationId,
    userAgent,
  })

  const { getSRPUrl, seoLocation } = useHomepageGetSeoUrl({
    locationArea: location.area,
    locationId,
  })

  return (
    <BaseLayout showSearchBar includeRUMScripts={true} pageName="homepage">
      <HomepageSeoMetadata
        seoLocation={seoLocation}
        locationId={locationId}
        locationName={location.name}
      />
      <HomepageAdsWrapper locationId={locationId} shouldShowHomepageFeed={shouldShowHomepageFeed}>
        {shouldShowMasthead && (
          <ClientRender>
            <GptMasthead />
          </ClientRender>
        )}

        <HomepageContainer>
          <HomepageMain>
            <HomepageH1 />

            <SimilarToFavouritesGallery locationId={locationId} />

            {!isUserLoggedIn || (!loadingFeed && !feedResults.length) ? (
              <FeedFallbackComponents
                getSeoUrl={getSRPUrl}
                location={location}
                userAgent={userAgent}
                isUserLoggedIn={isUserLoggedIn}
              />
            ) : (
              <HomepageSectionWrapper mTop={spacing.husky} mBottom={spacing.husky}>
                {priceDropToggle?.enabled && (
                  <Spacing mBottom={spacing.husky}>
                    <PriceDrop />
                  </Spacing>
                )}
                <HomepageFeed
                  getSeoUrl={getSRPUrl}
                  correlationId={correlationId}
                  feedPageInfo={feedPageInfo}
                  feedResults={feedResults}
                  handleLoadMore={handleLoadMore}
                  loading={hpFeedLoadingMore}
                  loadingFeed={loadingFeed}
                  locationId={locationId}
                  locationName={location.name[apiLocale]}
                  shouldAutoFetchMore={shouldAutoFetchMore}
                  userAgent={userAgent}
                  userData={userData}
                />
              </HomepageSectionWrapper>
            )}
          </HomepageMain>

          <HomepageAside>
            <HomepageGptRightrailTopWrapper>
              <ClientRender>
                <GptRightrailTop />
              </ClientRender>
            </HomepageGptRightrailTopWrapper>

            <GptRightrailMarketing />
          </HomepageAside>
        </HomepageContainer>

        <HomepageContainer>
          <ClientRender>
            <GptStickyHP />
          </ClientRender>
          {shouldShowHomepageFeed ? <ScrollToTopButton /> : null}
        </HomepageContainer>
      </HomepageAdsWrapper>
    </BaseLayout>
  )
}
